import React from "react";
import { IonButton, IonCol, IonIcon, IonLabel, IonRow, IonText } from "@ionic/react";
import { createOutline } from "ionicons/icons";

interface SummaryItemRowProps {
    /** Primary line (bold), e.g. `3 Stalls` or `Dates`. */
    label: string;
    /** Optional leading content shown before the label (e.g. status chip). */
    leading?: React.ReactNode;
    /** Optional value on the far right before the edit control. */
    value?: React.ReactNode;
    /** Shown just left of the edit control (e.g. `$—`). */
    costPlaceholder?: string;
    /** When set, shows an edit control that jumps to the related form section. */
    onEditClick?: () => void;
    /** When set, the whole row is clickable (no edit icon); e.g. jump to Waivers. */
    onRowClick?: () => void;
    /** When true, shows the Loading chip treatment and the shared blink animation. */
    isLoading?: boolean;
}

function renderValueNode(value: React.ReactNode) {
    if (value === undefined || value === null) {
        return null;
    }
    if (typeof value === "string" || typeof value === "number") {
        return <IonText className="text-dark font-weight-bold summary-item-row__label-text">{value}</IonText>;
    }
    return value;
}

/** Row style aligned with waiver rule rows: white bar, bold label, optional value and edit on the right. */
const SummaryItemRow: React.FC<SummaryItemRowProps> = ({ label, leading, value, costPlaceholder, onEditClick, onRowClick, isLoading = false }) => {
    const valueRendered = renderValueNode(value);
    const showValue = valueRendered !== null;
    const showCost = Boolean(costPlaceholder);
    const rowInteractive = Boolean(onRowClick);

    return (
        <IonRow
            className={`ion-align-items-center ion-justify-content-between bg-white px-2 py-2 mb-2 mx-0 summary-item-row${
                rowInteractive ? " summary-item-row--interactive" : ""
            }${isLoading ? " saving-blink-animation" : ""}`}
            onClick={onRowClick ? () => onRowClick() : undefined}
            onKeyDown={
                onRowClick
                    ? (e) => {
                          if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              onRowClick();
                          }
                      }
                    : undefined
            }
            role={rowInteractive ? "button" : undefined}
            tabIndex={rowInteractive ? 0 : undefined}
            aria-label={rowInteractive ? `Open ${label}` : undefined}
            aria-busy={isLoading || undefined}>
            <IonCol className="ion-no-padding summary-item-row__label-col">
                <IonLabel className="d-flex ion-align-items-center summary-item-row__label">
                    {leading ? <span className="d-flex ion-align-items-center">{leading}</span> : null}
                    <IonText className="font-weight-bold text-dark ion-text-wrap summary-item-row__label-text">{label}</IonText>
                </IonLabel>
            </IonCol>
            <IonCol size="auto" className="ion-no-padding summary-item-row__value-col">
                <IonRow className="ion-align-items-center ion-nowrap ion-justify-content-end">
                    {showValue ? <IonCol className="ion-no-padding d-flex ion-align-items-center ion-justify-content-end">{valueRendered}</IonCol> : null}
                    {showCost ? (
                        <IonCol size="auto" className="ion-no-padding d-flex ion-align-items-center pr-1">
                            <IonText className="text-dark font-weight-bold summary-item-row__cost">{costPlaceholder}</IonText>
                        </IonCol>
                    ) : null}
                    {onEditClick && !onRowClick && !isLoading ? (
                        <IonCol size="auto" className="ion-no-padding">
                            <IonButton
                                fill="clear"
                                size="small"
                                className="m-0 summary-item-row__edit-btn"
                                aria-label={`Edit ${label}`}
                                onClick={(e: React.MouseEvent<HTMLIonButtonElement>) => {
                                    e.stopPropagation();
                                    onEditClick();
                                }}>
                                <IonIcon icon={createOutline} className="text-primary summary-item-row__edit-icon" />
                            </IonButton>
                        </IonCol>
                    ) : null}
                </IonRow>
            </IonCol>
        </IonRow>
    );
};

export default SummaryItemRow;
